/**
 * User Notification Manager for Authentication and System Events
 * Provides elegant, non-intrusive notifications for fallback scenarios
 */

import * as React from 'react';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';
export type NotificationPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center';

export interface NotificationOptions {
  id?: string;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number; // milliseconds, 0 for persistent
  position?: NotificationPosition;
  showProgress?: boolean;
  actions?: NotificationAction[];
  metadata?: Record<string, any>;
}

export interface NotificationAction {
  label: string;
  action: () => void;
  style?: 'primary' | 'secondary' | 'danger';
}

export interface ActiveNotification extends NotificationOptions {
  id: string;
  timestamp: number;
  timeoutId?: NodeJS.Timeout;
}

/**
 * Centralized notification manager with queue and deduplication
 */
export class NotificationManager {
  private static instance: NotificationManager;
  private notifications = new Map<string, ActiveNotification>();
  private listeners = new Set<(notifications: ActiveNotification[]) => void>();
  private queue: NotificationOptions[] = [];
  private maxNotifications = 5;
  private defaultDuration = 5000; // 5 seconds
  
  static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager();
    }
    return NotificationManager.instance;
  }
  
  /**
   * Show a notification
   */
  show(options: NotificationOptions): string {
    const id = options.id || this.generateId();
    
    // Prevent duplicate notifications
    if (this.notifications.has(id)) {
      this.updateNotification(id, options);
      return id;
    }
    
    // If we're at max capacity, queue the notification
    if (this.notifications.size >= this.maxNotifications) {
      this.queue.push(options);
      return id;
    }
    
    const notification: ActiveNotification = {
      ...options,
      id,
      timestamp: Date.now(),
      duration: options.duration ?? this.defaultDuration,
      position: options.position ?? 'top-right'
    };
    
    // Set auto-dismiss timeout
    if (notification.duration && notification.duration > 0) {
      notification.timeoutId = setTimeout(() => {
        this.dismiss(id);
      }, notification.duration);
    }
    
    this.notifications.set(id, notification);
    this.notifyListeners();
    
    console.log(`[NotificationManager] Showing ${options.type} notification: ${options.message}`);
    return id;
  }
  
  /**
   * Update an existing notification
   */
  updateNotification(id: string, updates: Partial<NotificationOptions>): void {
    const existing = this.notifications.get(id);
    if (!existing) return;
    
    // Clear existing timeout
    if (existing.timeoutId) {
      clearTimeout(existing.timeoutId);
    }
    
    const updated: ActiveNotification = {
      ...existing,
      ...updates,
      id, // Preserve ID
      timestamp: existing.timestamp // Preserve original timestamp
    };
    
    // Set new timeout if duration changed
    const duration = updates.duration ?? existing.duration;
    if (duration && duration > 0) {
      updated.timeoutId = setTimeout(() => {
        this.dismiss(id);
      }, duration);
    }
    
    this.notifications.set(id, updated);
    this.notifyListeners();
  }
  
  /**
   * Dismiss a notification
   */
  dismiss(id: string): void {
    const notification = this.notifications.get(id);
    if (!notification) return;
    
    if (notification.timeoutId) {
      clearTimeout(notification.timeoutId);
    }
    
    this.notifications.delete(id);
    this.processQueue();
    this.notifyListeners();
    
    console.log(`[NotificationManager] Dismissed notification: ${id}`);
  }
  
  /**
   * Dismiss all notifications of a specific type
   */
  dismissByType(type: NotificationType): void {
    const toRemove: string[] = [];
    
    for (const [id, notification] of Array.from(this.notifications.entries())) {
      if (notification.type === type) {
        toRemove.push(id);
      }
    }
    
    toRemove.forEach(id => this.dismiss(id));
  }
  
  /**
   * Clear all notifications
   */
  clear(): void {
    for (const id of Array.from(this.notifications.keys())) {
      this.dismiss(id);
    }
    this.queue = [];
  }
  
  /**
   * Subscribe to notification changes
   */
  subscribe(listener: (notifications: ActiveNotification[]) => void): () => void {
    this.listeners.add(listener);
    
    // Immediately call with current notifications
    listener(Array.from(this.notifications.values()));
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }
  
  /**
   * Get all active notifications
   */
  getNotifications(): ActiveNotification[] {
    return Array.from(this.notifications.values())
      .sort((a, b) => b.timestamp - a.timestamp);
  }
  
  /**
   * Process queued notifications
   */
  private processQueue(): void {
    while (this.queue.length > 0 && this.notifications.size < this.maxNotifications) {
      const queued = this.queue.shift();
      if (queued) {
        this.show(queued);
      }
    }
  }
  
  /**
   * Notify all listeners of changes
   */
  private notifyListeners(): void {
    const notifications = this.getNotifications();
    this.listeners.forEach(listener => {
      try {
        listener(notifications);
      } catch (error) {
        console.error('[NotificationManager] Listener error:', error);
      }
    });
  }
  
  /**
   * Generate unique notification ID
   */
  private generateId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * Pre-configured notification functions for common authentication scenarios
 */
export const AuthNotifications = {
  /**
   * Token refresh in progress
   */
  tokenRefreshing: (): string => {
    return NotificationManager.getInstance().show({
      id: 'token-refreshing',
      type: 'info',
      title: 'Refreshing Session',
      message: 'Updating your authentication...',
      duration: 3000,
      showProgress: true
    });
  },
  
  /**
   * Token refresh successful
   */
  tokenRefreshSuccess: (): string => {
    return NotificationManager.getInstance().show({
      id: 'token-refresh-success',
      type: 'success',
      title: 'Session Updated',
      message: 'Authentication refreshed successfully',
      duration: 2000
    });
  },
  
  /**
   * Session expired, user action needed
   */
  sessionExpired: (onSignIn?: () => void): string => {
    const actions: NotificationAction[] = [];
    
    if (onSignIn) {
      actions.push({
        label: 'Sign In',
        action: onSignIn,
        style: 'primary'
      });
    }
    
    return NotificationManager.getInstance().show({
      id: 'session-expired',
      type: 'warning',
      title: 'Session Expired',
      message: 'Please sign in to continue',
      duration: 0, // Persistent
      actions
    });
  },
  
  /**
   * Network connectivity issues
   */
  networkError: (): string => {
    return NotificationManager.getInstance().show({
      id: 'network-error',
      type: 'error',
      title: 'Connection Issue',
      message: 'Please check your internet connection',
      duration: 5000
    });
  },
  
  /**
   * Service temporarily unavailable
   */
  serviceUnavailable: (): string => {
    return NotificationManager.getInstance().show({
      id: 'service-unavailable',
      type: 'warning',
      title: 'Service Unavailable',
      message: 'The service is temporarily unavailable. Please try again later.',
      duration: 7000
    });
  },
  
  /**
   * Operation degraded to read-only mode
   */
  readOnlyMode: (): string => {
    return NotificationManager.getInstance().show({
      id: 'read-only-mode',
      type: 'info',
      title: 'Limited Access',
      message: 'Viewing in read-only mode. Sign in for full functionality.',
      duration: 6000
    });
  },
  
  /**
   * Draft saved when send failed
   */
  draftSaved: (): string => {
    return NotificationManager.getInstance().show({
      id: 'draft-saved',
      type: 'info',
      title: 'Message Saved',
      message: 'Your message was saved as a draft',
      duration: 4000
    });
  },
  
  /**
   * Retry operation in progress
   */
  retrying: (attempt: number, maxAttempts: number): string => {
    return NotificationManager.getInstance().show({
      id: 'retrying-operation',
      type: 'info',
      title: 'Retrying',
      message: `Attempting to reconnect (${attempt}/${maxAttempts})...`,
      duration: 2000,
      showProgress: true
    });
  },
  
  /**
   * Generic authentication error
   */
  authError: (message?: string): string => {
    return NotificationManager.getInstance().show({
      type: 'error',
      title: 'Authentication Error',
      message: message || 'An authentication error occurred',
      duration: 5000
    });
  }
};

/**
 * Convenience functions for common notification patterns
 */
export const notify = {
  info: (message: string, options?: Partial<NotificationOptions>) => 
    NotificationManager.getInstance().show({ type: 'info', message, ...options }),
    
  success: (message: string, options?: Partial<NotificationOptions>) => 
    NotificationManager.getInstance().show({ type: 'success', message, ...options }),
    
  warning: (message: string, options?: Partial<NotificationOptions>) => 
    NotificationManager.getInstance().show({ type: 'warning', message, ...options }),
    
  error: (message: string, options?: Partial<NotificationOptions>) => 
    NotificationManager.getInstance().show({ type: 'error', message, ...options }),
};

/**
 * React Hook for using notifications in components
 */
export const useNotifications = () => {
  const [notifications, setNotifications] = React.useState<ActiveNotification[]>([]);
  
  React.useEffect(() => {
    const manager = NotificationManager.getInstance();
    const unsubscribe = manager.subscribe(setNotifications);
    return unsubscribe;
  }, []);
  
  return {
    notifications,
    show: (options: NotificationOptions) => NotificationManager.getInstance().show(options),
    dismiss: (id: string) => NotificationManager.getInstance().dismiss(id),
    clear: () => NotificationManager.getInstance().clear(),
    auth: AuthNotifications,
    notify
  };
};
